import { CandidatePath, CandidatePathProductRef } from '../model/candidate-path.model';
import { Lilate } from '../model/lilate.model';

const DONE_STATUSES = new Set(['done', 'terminated', 'passed']);
const CANCELLED_STATUSES = new Set(['cancelled', 'cancelled_by_admin']);

/** @deprecated Conservé pour compat ; le LILATE n'est plus bloqué par le FlashLevel. */
export const BUNDLE_LILATE_BLOCKED_I18N_KEY = 'levelup.lilate.blocked.flashlevel-required';

/** Message lorsque le FlashLevel est bloqué après un LILATE passé (règle A7). */
export const LEVELUP_FLASHLEVEL_BLOCKED_I18N_KEY = 'levelup.flashlevel.blocked.lilate-done';

export type ServiceBadge = 'levelup' | 'lilate' | 'flashlevel';
export type ProductContext = 'lilate' | 'flashlevel';

/** Parcours LevelUp (cible) ou bundle (legacy ES). */
export function isLevelUpPath(path?: CandidatePath | null): boolean {
    return path?.path_type === 'levelup' || path?.path_type === 'bundle';
}

/**
 * Badge service toujours renseigné pour le support Manage (jamais null).
 * Spec levelup-manage-v2 §2.1.
 */
export function resolveServiceBadge(
    path: CandidatePath | null | undefined,
    productContext: ProductContext,
): ServiceBadge {
    if (isLevelUpPath(path)) {
        return 'levelup';
    }
    if (path?.path_type === 'lilate') {
        return 'lilate';
    }
    if (path?.path_type === 'flashlevel') {
        return 'flashlevel';
    }
    return productContext;
}

export function serviceBadgeLabel(badge: ServiceBadge): string {
    if (badge === 'levelup') {
        return 'LevelUp';
    }
    if (badge === 'flashlevel') {
        return 'FlashLevel';
    }
    return 'LILATE';
}

/**
 * Détermine si le FlashLevel d'un LevelUp est terminé.
 */
export function isFlashlevelDone(flashlevel?: CandidatePathProductRef | null): boolean {
    if (!flashlevel?.status) {
        return false;
    }
    return DONE_STATUSES.has(flashlevel.status);
}

/** FlashLevel annulé (ex. partenaire passé au LILATE seul). */
export function isFlashlevelCancelled(flashlevel?: CandidatePathProductRef | null): boolean {
    return !!flashlevel?.status && CANCELLED_STATUSES.has(flashlevel.status);
}

/**
 * Parcours présenté au candidat comme un LevelUp. Un LevelUp dont le
 * FlashLevel est annulé se présente comme un LILATE seul : le candidat ne voit
 * plus le FlashLevel (Manage garde la vue LevelUp, via `isLevelUpPath`).
 */
export function isLevelUpPathForCandidate(path?: CandidatePath | null): boolean {
    return isLevelUpPath(path) && !isFlashlevelCancelled(path?.flashlevel);
}

/** LILATE terminé (liste past / Mes résultats). */
export function isLilateDone(lilate?: { status?: string } | null): boolean {
    if (!lilate?.status) {
        return false;
    }
    return lilate.status === 'done';
}

/**
 * True si le candidat peut démarrer / réserver le LILATE du parcours.
 * Le prérequis FlashLevel a été levé : toujours autorisé pour un path valide.
 */
export function canStartLilateForPath(path?: CandidatePath | null): boolean {
    if (!path) {
        return true;
    }
    if (typeof path.can_start_lilate === 'boolean') {
        return path.can_start_lilate;
    }
    return true;
}

/**
 * True si le FlashLevel du LevelUp peut encore être passé.
 * Bloqué lorsque le LILATE est déjà done (règle A7).
 * Le LILATE local done prime sur un can_start_flashlevel API à true
 * (documents ES legacy path_type=bundle non encore réindexés).
 */
export function canStartFlashlevelForPath(path?: CandidatePath | null): boolean {
    if (!path || !isLevelUpPath(path)) {
        return true;
    }
    if (isLilateDone(path.lilate)) {
        return false;
    }
    if (typeof path.can_start_flashlevel === 'boolean') {
        return path.can_start_flashlevel;
    }
    return true;
}

/**
 * Afficher la carte FlashLevel dans le parcours candidat.
 * Sur LevelUp, la carte reste visible même si le démarrage est bloqué (message A7),
 * sauf si le FlashLevel est annulé.
 */
export function shouldShowFlashlevelForPath(path?: CandidatePath | null): boolean {
    if (!path?.flashlevel || isFlashlevelCancelled(path.flashlevel)) {
        return false;
    }
    if (isLevelUpPath(path)) {
        return true;
    }
    return canStartFlashlevelForPath(path);
}

/**
 * Variante lorsque le LILATE porte directement les infos du parcours LevelUp.
 */
export function canStartLilate(lilate: Lilate & { path?: CandidatePath | null }): boolean {
    return canStartLilateForPath(lilate.path);
}

/**
 * Libellé UX lorsque le LILATE est bloqué (ne devrait plus arriver).
 * Retourne une clé i18n (à traduire côté composant).
 */
export function getLilateBlockedMessageKey(path?: CandidatePath | null): string | null {
    if (canStartLilateForPath(path)) {
        return null;
    }
    return BUNDLE_LILATE_BLOCKED_I18N_KEY;
}

/** @deprecated Utiliser getLilateBlockedMessageKey + translate */
export function getLilateBlockedMessage(path?: CandidatePath | null): string | null {
    if (canStartLilateForPath(path)) {
        return null;
    }
    return 'Vous devez passer votre FlashLevel avant de passer votre LILATE.';
}

/**
 * Clé i18n si le FlashLevel est bloqué parce que le LILATE est déjà passé (règle A7).
 * Un FlashLevel annulé est aussi bloqué, mais pas pour cette raison : pas de clé.
 */
export function getFlashlevelBlockedMessageKey(path?: CandidatePath | null): string | null {
    if (!path || !isLevelUpPath(path) || isFlashlevelCancelled(path.flashlevel) || canStartFlashlevelForPath(path)) {
        return null;
    }
    return LEVELUP_FLASHLEVEL_BLOCKED_I18N_KEY;
}

/** Message FR du bandeau LevelUp (Manage) : FlashLevel annulé, ou bloqué après un LILATE passé. */
export function getFlashlevelBlockedMessage(path?: CandidatePath | null): string | null {
    if (isLevelUpPath(path) && isFlashlevelCancelled(path?.flashlevel)) {
        return 'FlashLevel annulé : le parcours se poursuit avec le LILATE seul.';
    }
    if (!getFlashlevelBlockedMessageKey(path)) {
        return null;
    }
    return 'Le LILATE a déjà été réalisé : le FlashLevel n\'est plus accessible sur ce parcours.';
}

/**
 * Couleurs produit (spec §8) pour les cartes parcours.
 */
export const PRODUCT_COLORS = {
    flashlevel: { bg: '#eff4ff', border: '#dce8fc' },
    lilate: { bg: '#f0fbf6', border: '#bbf0d6' },
} as const;
